import { useEffect, useMemo, useState } from "react";
import { formatDateTime } from "../lib/dateFormat";
import { toast } from "sonner";
import { ArchiveRestore, Search } from "lucide-react";
import { api, formatErr } from "../lib/api";

const muted = { color: "var(--c-text-muted)" };

function fmt(iso) {
  try { return formatDateTime(iso); } catch (_) { return iso; }
}

// Soft-deleted access roles; Super Admin can restore them (optionally with their previous users).
export default function DeletedRoles({ onRestored }) {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [restoreUsers, setRestoreUsers] = useState({}); // roleId -> bool
  const [busy, setBusy] = useState("");

  async function load() {
    try {
      setLoading(true);
      const r = await api.get("/access-management/deleted-roles");
      setRoles(r.data.roles || []);
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  const visible = useMemo(() => {
    const q = filter.toLowerCase().trim();
    return q ? roles.filter((r) => r.name.toLowerCase().includes(q)) : roles;
  }, [roles, filter]);

  async function restore(role) {
    const withUsers = restoreUsers[role.id] ?? true;
    try {
      setBusy(role.id);
      const r = await api.post(`/access-management/roles/${role.id}/restore`, { restore_users: withUsers });
      toast.success(`Restored "${role.name}"${r.data.restored_users ? ` with ${r.data.restored_users} user(s)` : ""}`);
      await load();
      onRestored?.();
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setBusy("");
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="text-[12.5px]" style={muted}>
          Deleted roles are kept here. Restoring brings back the role with the screens it had when it was deleted.
        </div>
        <div className="relative w-full md:w-[280px]">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2" style={muted} />
          <input className="input w-full !pl-8" placeholder="Search deleted roles" value={filter} onChange={(e) => setFilter(e.target.value)} />
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="tbl">
          <thead>
            <tr>
              <th>Role</th>
              <th>Screens</th>
              <th>Deleted</th>
              <th>Users at deletion</th>
              <th className="text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={5} className="text-center py-8" style={muted}>Loading…</td></tr>}
            {!loading && visible.map((r) => {
              const moved = (r.moved_user_ids || []).length;
              return (
                <tr key={r.id}>
                  <td className="align-top">
                    <div className="font-medium">{r.name}</div>
                    <div className="text-[11.5px]" style={muted}>{r.source === "erp" ? `ERP role #${r.erp_role_id}` : "Custom role"}</div>
                  </td>
                  <td className="align-top text-[12px]">{(r.screens || []).length} screens</td>
                  <td className="align-top text-[12px]">
                    <div>{fmt(r.deleted_at)}</div>
                    <div style={muted}>by {r.deleted_by || "—"}</div>
                  </td>
                  <td className="align-top text-[12px]">
                    {moved ? (
                      <>
                        <div>{moved} user(s) moved to {r.moved_to_name || "a deleted role"}</div>
                        <label className="inline-flex items-center gap-1.5 mt-1 cursor-pointer">
                          <input type="checkbox" checked={restoreUsers[r.id] ?? true}
                            onChange={(e) => setRestoreUsers((m) => ({ ...m, [r.id]: e.target.checked }))} />
                          Move them back on restore
                        </label>
                      </>
                    ) : <span style={muted}>None</span>}
                  </td>
                  <td className="align-top text-right">
                    <button className="btn btn-outline" onClick={() => restore(r)} disabled={busy === r.id}>
                      <ArchiveRestore size={14} /> {busy === r.id ? "Restoring…" : "Restore"}
                    </button>
                  </td>
                </tr>
              );
            })}
            {!loading && !visible.length && (
              <tr><td colSpan={5} className="text-center py-8" style={muted}>No deleted roles.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
